import { BarsArrowDownIcon, ArrowDownTrayIcon, CurrencyDollarIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button, ButtonGroup, Grid2, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FC, useState, useEffect } from "react";
import i18n from "../i18n/i18n";
import { statusFilterOptions } from "../utils/filter";
import {
  FilterReportEntries,
  useSendAllToFinancialValidationById,
  useSendAllToPaid,
  useExportReportEntriesSheet,
  useExportReportEntriesFiles,
  useGetPendingAdvances,
} from "../service/reportEntry";
import { formatCurrency } from "../utils/maskFormatMoney";
import Collaborator from "../@types/Collaborator";
import { FilterValues } from "../@types/filter";
import Modal from "./atoms/Modal";
import Loading from "./atoms/Loading";
import { PendingAdvancesAlert } from "./molecules/PendingAdvancesAlert";

interface RefundReportTableButtonsProps {
  reportData?: FilterReportEntries;
  loggedUser?: Collaborator;
  filterValues?: FilterValues;
  collaborators?: Collaborator[];
  showTotalValue: boolean;
  showExportFilesButtons: boolean;
  showSendToButtons: boolean;
  showAddReportEntryButton: boolean;
  page: number;
  size: number;
}

export const RefundReportTableButtons: FC<RefundReportTableButtonsProps> = ({
  filterValues,
  loggedUser,
  showTotalValue,
  showExportFilesButtons,
  showSendToButtons,
  showAddReportEntryButton,
  collaborators,
}) => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [action, setAction] = useState("");
  const [dialogTitle, setDialogTitle] = useState("Title");
  const [dialogText, setDialogText] = useState("Text");

  const { mutate: sendAllToFinancialValidation } = useSendAllToFinancialValidationById();
  const { mutate: sendAllToPaid } = useSendAllToPaid();
  const { mutate: exportSheet, isLoading: isExportingSheet } = useExportReportEntriesSheet();
  const { mutate: exportFilesRequest, isLoading: isExportingFiles } = useExportReportEntriesFiles();

  const isExporting = isExportingSheet || isExportingFiles;
  const isExportingAction = action === "exportSheet" || action === "exportFiles";
  const {
    data: pendingAdvances,
    isFetching: isFetchingPendingAdvances,
    refetch: refetchPendingAdvances,
  } = useGetPendingAdvances({
    ...filterValues,
    isAdvance: true,
    statusFilter:
      action === "sendAllToFinancialValidation"
        ? statusFilterOptions.approved
        : statusFilterOptions.financialValidation,
  } as FilterValues);

  const hasPendingAdvances = (pendingAdvances?.length ?? 0) > 0;
  const pendingAdvancesCount = pendingAdvances?.length ?? 0;

  const onRequestAction = (action: string) => {
    switch (action) {
      case "sendAllToFinancialValidation":
        sendAllToFinancialValidation(filterValues || ({} as FilterValues), {
          onSuccess: () => {
            setOpenDialog(false);
            setAction("");
          },
          onError: (error: Error) => {
            toast.error(error.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          },
        });
        break;
      case "sendAllToPaid":
        sendAllToPaid(filterValues, {
          onSuccess: () => {
            setOpenDialog(false);
            setAction("");
          },
          onError: (error: Error) => {
            toast.error(error.message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          },
        });
        break;
      case "exportSheet":
        exportSheet(
          { filters: filterValues || null },
          {
            onSuccess: () => {
              setAction("");
            },
            onError: () => {
              setAction("");
            },
          },
        );
        break;
      case "exportFiles":
        exportFilesRequest(
          { filters: filterValues || null },
          {
            onSuccess: () => {
              setAction("");
            },
            onError: () => {
              setAction("");
            },
          },
        );
        break;
    }
  };

  useEffect(() => {
    if (action === "sendAllToFinancialValidation" || action === "sendAllToPaid") {
      refetchPendingAdvances();
    }
  }, [action]);

  console.log("[DEBUG]", showTotalValue);

  return (
    <Grid2
      container
      spacing={1}
      sx={{
        position: "sticky",
        right: 0,
        justifyContent: "flex-end",
        backgroundColor: "white",
        zIndex: 10,
        padding: "8px 0",
      }}
    >
      {showTotalValue && (
        <>
          <ButtonGroup
            variant="contained"
            aria-label="Basic button group"
            sx={{
              borderRadius: "8px",
            }}
          >
            <Button
              sx={{
                display: "flex",
                backgroundColor: "#9EC9C9",
                color: "#00373E",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.600rem",
                "&:hover": {
                  backgroundColor: "#9EC9C9",
                  cursor: "default",
                },
              }}
            >
              {i18n.t("refundReportTableButtons.totals.refunds")}
            </Button>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#00373E",
                fontSize: "0.780rem",
                padding: 1,
              }}
            >
              {formatCurrency(Number(collaborators?.[0]?.totalValue || 0))}
            </Typography>
          </ButtonGroup>

          <ButtonGroup
            variant="contained"
            aria-label="Basic button group"
            sx={{
              borderRadius: "8px",
            }}
          >
            <Button
              sx={{
                display: "flex",
                backgroundColor: "#9EC9C9",
                color: "#00373E",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.600rem",
                "&:hover": {
                  backgroundColor: "#9EC9C9",
                  cursor: "default",
                },
              }}
            >
              {i18n.t("refundReportTableButtons.totals.advances")}
            </Button>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#00373E",
                fontSize: "0.780rem",
                padding: 1,
              }}
            >
              {formatCurrency(Number(collaborators?.[0]?.totalAdvanceValue || 0), true)}
            </Typography>
          </ButtonGroup>
        </>
      )}

      <ButtonGroup variant="contained" aria-label="Basic button group" color="inherit">
        {showExportFilesButtons && (
          <Tooltip title={i18n.t("refundReportTableButtons.export.sheet.tooltip")}>
            <Button
              sx={{
                display: "flex",
                backgroundColor: "#0C7878",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.600rem",
              }}
              onClick={() => {
                if (isExporting) {
                  toast.error(i18n.t("refundReportTableButtons.exportInProgress"), {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                  return;
                }
                setDialogTitle(i18n.t("refundReportTableButtons.export.sheet.dialog.title"));
                setDialogText(i18n.t("refundReportTableButtons.export.sheet.dialog.text"));
                setAction("exportSheet");
                setOpenDialog(true);
              }}
            >
              <BarsArrowDownIcon className="w-6 h-6 text-white" />
            </Button>
          </Tooltip>
        )}

        {showExportFilesButtons && loggedUser?.financialSupport && (
          <Tooltip title={i18n.t("refundReportTableButtons.export.files.tooltip")}>
            <Button
              sx={{
                display: "flex",
                backgroundColor: "#0C7878",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.600rem",
              }}
              onClick={() => {
                if (isExporting) {
                  toast.error(i18n.t("refundReportTableButtons.exportInProgress"), {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                  return;
                }
                setDialogTitle(i18n.t("refundReportTableButtons.export.files.dialog.title"));
                setDialogText(i18n.t("refundReportTableButtons.export.files.dialog.text"));
                setAction("exportFiles");
                setOpenDialog(true);
              }}
            >
              <ArrowDownTrayIcon className="w-6 h-6 text-white" />
            </Button>
          </Tooltip>
        )}

        {showSendToButtons && loggedUser?.financialSupport && (
          <Tooltip title={i18n.t("refundReportTableButtons.sendAllToFinancialValidation.tooltip")}>
            <Button
              disabled={isFetchingPendingAdvances && action === "sendAllToFinancialValidation"}
              loading={isFetchingPendingAdvances && action === "sendAllToFinancialValidation"}
              sx={{
                display: "flex",
                backgroundColor: "#0C7878",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.600rem",
              }}
              onClick={() => {
                setDialogTitle(i18n.t("refundReportTableButtons.sendAllToFinancialValidation.dialog.title"));
                setDialogText(i18n.t("refundReportTableButtons.sendAllToFinancialValidation.dialog.text"));
                setAction("sendAllToFinancialValidation");
                setOpenDialog(true);
              }}
            >
              {isFetchingPendingAdvances && action === "sendAllToFinancialValidation" ? (
                <Loading size="small" />
              ) : (
                <CheckCircleIcon className="w-6 h-6 text-white" />
              )}
            </Button>
          </Tooltip>
        )}

        {showSendToButtons && loggedUser?.financialSupport && (
          <Tooltip title={i18n.t("refundReportTableButtons.sendAllToPaid.tooltip")}>
            <Button
              disabled={isFetchingPendingAdvances && action === "sendAllToPaid"}
              loading={isFetchingPendingAdvances && action === "sendAllToPaid"}
              sx={{
                display: "flex",
                backgroundColor: "#0C7878",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.600rem",
              }}
              onClick={() => {
                setDialogTitle(i18n.t("refundReportTableButtons.sendAllToPaid.dialog.title"));
                setDialogText(i18n.t("refundReportTableButtons.sendAllToPaid.dialog.text"));
                setAction("sendAllToPaid");
                setOpenDialog(true);
                refetchPendingAdvances();
              }}
            >
              {isFetchingPendingAdvances && action === "sendAllToPaid" ? (
                <Loading size="small" />
              ) : (
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              )}
            </Button>
          </Tooltip>
        )}
      </ButtonGroup>

      {showAddReportEntryButton && (
        <Button
          onClick={() => {
            navigate("/new_request");
          }}
          sx={{
            fontSize: "0.600rem",
            backgroundColor: "#0C7878",
            color: "white",
            transition: "scale 0.2s ease-in-out",
            "&:hover": {
              scale: 1.05,
            },
          }}
        >
          {i18n.t("newRequest")}
        </Button>
      )}

      {showAddReportEntryButton && (
        <Button
          onClick={() => {
            navigate("/new_advance_payment");
          }}
          sx={{
            fontSize: "0.600rem",
            backgroundColor: "#0C7878",
            color: "white",
            transition: "scale 0.2s ease-in-out",
            "&:hover": {
              scale: 1.05,
            },
          }}
        >
          {i18n.t("newAdvance")}
        </Button>
      )}

      <Modal
        title={dialogTitle}
        isOpen={openDialog && !isFetchingPendingAdvances}
        setIsOpen={setOpenDialog}
        body={
          <div className="flex flex-col gap-2 mt-2">
            <p className="text-sm text-gray-500">{dialogText}</p>
            {hasPendingAdvances && (
              <PendingAdvancesAlert
                pendingCount={pendingAdvancesCount}
                message={i18n.t(
                  `refundReportTableButtons.pendingAdvancesAlert.${action}.${pendingAdvancesCount > 1 ? "plural" : "singular"}`,
                  { count: pendingAdvancesCount }
                )}
              />
            )}
          </div>
        }
        bottom={
          <div className="flex flex-col my-2">
            <div className="flex flex-row justify-around mt-4">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onClick={() => {
                if (isExporting && isExportingAction) {
                  toast.error(i18n.t("refundReportTableButtons.exportInProgress"), {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                  return;
                }
                  if (isExportingAction) {
                    setOpenDialog(false);
                  }
                  onRequestAction(action);
                }}
              >
                {i18n.t("refundReportTableButtons.modal.confirm")}
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-transparent bg-red-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                onClick={() => {
                  setOpenDialog(false);
                  setAction("");
                }}
              >
                {i18n.t("refundReportTableButtons.modal.cancel")}
              </button>
            </div>
          </div>
        }
      />
    </Grid2>
  );
};
